<!--
  AuthLayout.svelte — Shared split-screen layout for all auth pages.

  Left half: branded gradient with logo + tagline (hidden on mobile).
  Right half: centered card containing the auth form (slot content).

  Every auth page (Login, Register, ForgotPassword, etc.) is wrapped
  in this layout so the branding stays consistent across the flow.

  @slot default — The form content rendered inside the right-side card.
-->
<script lang="ts">
	import site from '../../site';
	import { configurationStore } from '../../AppConfiguration';
	import { colorTheme } from '../../Auth';
	import InteractiveGrid from '../../components/InteractiveGrid.svelte';
	import { t } from '../../i18n';
	import LanguageSelector from '../../components/LanguageSelector.svelte';




	interface Props {
		/** Heading displayed above the form slot. */
		heading?: string;
		/** Optional sub-heading displayed below the heading. */
		subheading?: string;
		children?: import('svelte').Snippet;
	}

	let { heading = '', subheading = '', children }: Props = $props();
</script>

<div class="flex min-h-screen">
	<!-- ── Left panel: branding (hidden on small screens) ────────── -->
	<div
		class="auth-hero relative hidden items-center justify-center overflow-hidden lg:flex lg:w-1/2"
	>
		<!-- Interactive wireframe grid background -->
		<InteractiveGrid />

		<div class="hero-text pointer-events-none relative z-10 max-w-lg px-12 text-center">
			<img src="/LogoTextWide-Black.png" alt={site.name} class="mx-auto mb-8 h-12 invert" />
			<h1 class="mb-4 text-3xl font-bold text-white">
				{$configurationStore?.loginHeading || $t('Identity, simplified.')}
			</h1>
			<p class="text-lg leading-relaxed text-primary-200">
				{$configurationStore?.loginSubheading ||
					$t('Secure authentication, role management, and user administration \u2014 all in one place.')}
			</p>
		</div>
	</div>

	<!-- ── Right panel: auth form ──────────────────────────────── -->
	<div class="flex w-full flex-col bg-white lg:w-1/2 dark:bg-gray-900">
		<!-- Top-right dark mode toggle -->
		<div class="flex items-center justify-between p-4">
			<a href="/" class="flex items-center gap-2 lg:hidden">
				<img src="/LogoTextWide-Black.png" alt={site.name} class="h-6 dark:invert" />
			</a>
			<div class="ml-auto flex items-center gap-1">
				<!-- Language selector -->
				<LanguageSelector buttonClass="h-9 p-2" />
				<!-- Dark mode toggle -->
				<button
					type="button"
					aria-label={$t("Toggle dark mode")}
					class="rounded-lg p-2.5 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
					onclick={() => { $colorTheme = $colorTheme === 'dark' ? 'light' : 'dark'; }}
				>
					<i class="fa-solid {$colorTheme === 'dark' ? 'fa-sun' : 'fa-moon'} h-5 w-5"></i>
				</button>
			</div>
		</div>

		<!-- Centered form card -->
		<div class="flex flex-1 items-center justify-center px-6 pb-12">
			<div class="w-full max-w-md">
				{#if heading}
					<h2 class="mb-1 text-2xl font-bold text-gray-900 dark:text-white">{heading}</h2>
				{/if}
				{#if subheading}
					<p class="mb-6 text-gray-500 dark:text-gray-400">{subheading}</p>
				{/if}

				{@render children?.()}
			</div>
		</div>

		<!-- Footer -->
		<div class="space-x-2 pb-4 text-center text-xs text-gray-400 dark:text-gray-600">
			<span>
				&copy; {new Date().getFullYear()}
				{#if $configurationStore?.website}
					<a
						href={$configurationStore.website}
						target="_blank"
						rel="noopener noreferrer"
						class="hover:underline">{$configurationStore.websiteName || $t('Website')}</a
					>.
				{:else}
					<a href="https://abstract3d.com" target="_blank" class="hover:underline">Abstract</a>.
				{/if}
				{$t("All rights reserved.")}
			</span>
			{#if $configurationStore?.termsOfServiceURL}
				<span class="mx-1">&middot;</span>
				<a
					href={$configurationStore.termsOfServiceURL}
					target="_blank"
					rel="noopener noreferrer"
					class="hover:underline">{$t("Terms of Service")}</a
				>
			{/if}
		</div>
	</div>
</div>

<style>
	/*
	 * The branding panel's own palette, not a slice of `primary`.
	 *
	 * `primary` is Tailwind's blue — right for a button, a link, a focus ring — but a half-screen
	 * gradient of `primary-600 → primary-800` is a flat, saturated royal blue, the loudest part of
	 * the ramp at the largest size the app ever draws it. The panel instead starts at the Abstract
	 * accent (`#007dd1`, a cleaner cerulean) and settles through a deep ocean into a slightly
	 * desaturated navy: one hue family, so it reads as the brand, darkening rather than shifting.
	 * The tagline glow is the same navy, so the text sits in a pool of the panel's own colour.
	 */
	.auth-hero {
		--hero-from: var(--color-accent);
		--hero-via: #0b4a86;
		--hero-to: #0b2240;
		--hero-glow: rgba(9, 32, 64, 0.82);
		background: linear-gradient(135deg, var(--hero-from) 0%, var(--hero-via) 55%, var(--hero-to) 100%);
	}

	:global(.dark) .auth-hero {
		--hero-from: #005a99;
		--hero-via: #0a3360;
		--hero-to: #06142a;
		--hero-glow: rgba(5, 16, 34, 0.9);
	}

	/* Radial gradient mask behind hero text — fades the grid so text is readable. */
	.hero-text::before {
		content: '';
		position: absolute;
		inset: -80% -40%;
		z-index: -1;
		border-radius: 50%;
		background: radial-gradient(ellipse at center, var(--hero-glow) 0%, transparent 70%);
		pointer-events: none;
	}
</style>
